import React, {FunctionComponent, useCallback, useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {Label} from "reactstrap";
import {orderService} from "services/OrderService";
import usePaginatedData from "../../../hooks/usePaginatedData";
import {OrderSearchObject, OrderState} from "../../../interfaces/OrderInterfaces";
import {customerService} from "../../../services/CustomerService";
import DateFilter from "../filters/DateFilter";
import FilterGroup from "../filters/FilterGroup";
import Pagination from "../pagination/Pagination";
import OrderTable from "../table/order/OrderTable";
import Button from "../../atoms/Button";
import {ColorType} from "../../../types/bootstrap/BootstrapType";
import AutocompleteFilterPageable from "../filters/AutocompleteFilterPageable";
import {profileUtils} from "../../../utils/profileUtils";
import Spinner from "components/atoms/Spinner";
import TextInput from "../../atoms/input/TextInput";

interface OrderListProps {
  className?: string
  searchObject?: OrderSearchObject,
  updateSearchField?: (field: string, value: string) => void
  onReset?: () => void,
  itemPrefixPath?: string,
}

const OrderList: FunctionComponent<OrderListProps> = ({
  className = "",
  searchObject,
  updateSearchField,
  onReset,
  itemPrefixPath
}) => {
  const intl = useIntl();
  const [localSearchValue, setLocalSearchValue] = useState(searchObject?.customerOrCode || "");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setLocalSearchValue(searchObject?.customerOrCode || "");
  }, [searchObject?.customerOrCode]);

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearchValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (value.length >= 3 || value.length === 0) {
        updateSearchField("customerOrCode", value);
      }
    }, 500);
  }, [updateSearchField]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const {
    data,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortOptions,
    setSortOptions,
    refresh,
    total,
    totalElements,
    loading
  } = usePaginatedData(orderService.getOrdersPage, searchObject)

  return (
    <div className={`mb-5 ${className ?? ""}`}>
      <FilterGroup className="d-flex gap-2">
        <div className="epow-filter main-filter">
          <div className="epow-label">
            <Label>{intl.formatMessage({id: "order_filter_code_title"})}</Label>
          </div>
          <TextInput
            value={localSearchValue}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={intl.formatMessage({id: "order_filter_code_placeholder"})}
          />
        </div>

        <AutocompleteFilterPageable
          onChange={(value) => updateSearchField("customerId", value)}
          className="order_filter_client"
          title={intl.formatMessage({id: "order_filter_customer"})}
          value={searchObject?.customerId}
          fetchData={customerService.getCustomersPage}
          filterFieldName="name"
        />

        <DateFilter
          value={searchObject.startDate}
          onChangeDate={(value) => updateSearchField("startDate", value)}
          title={intl.formatMessage({id: "order_filter_period_start"})}
        />

        <DateFilter
          value={searchObject.endDate}
          onChangeDate={(value) => updateSearchField("endDate", value)}
          title={intl.formatMessage({id: "order_filter_period_end"})}
        />
        <div>
          <Button onClick={onReset} color={ColorType.SECONDARY} >{intl.formatMessage({id: "header_menu_clear_search"})}</Button>
        </div>
      </FilterGroup>

      {loading ? (
        <Spinner />
      ) : (
        <>
        <OrderTable
          orders={data}
          total={totalElements}
          sortOptions={sortOptions}
          canEdit={profileUtils.isAdmin() || profileUtils.isInterne()}
          refresh={refresh}
          onSortOptions={setSortOptions}
          itemPrefixPath={itemPrefixPath}
          isSelectable={OrderState.DRAFT === searchObject.status}
        />
        <Pagination page={page} setPage={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} total={total} />
        </>
      )}
  </div>
  )
}

export default OrderList
